export function showDocumentDeleteFailedMessage(
  err: unknown,
  fallback: string = 'Something went wrong.'
): string {
  if (typeof err === 'string') return err;
  if (err instanceof Error) return err.message;
  return fallback;
}

const TIFF_EXTENSIONS = ['.tiff', '.tif'];

export function isTiffFileName(fileName: string | undefined): boolean {
  if (!fileName || typeof fileName !== 'string') return false;
  const lower = fileName.toLowerCase();
  return TIFF_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

export function isPdfFileName(fileName: string | undefined): boolean {
  if (!fileName || typeof fileName !== 'string') return false;
  return fileName.toLowerCase().endsWith('.pdf');
}

export function findSelectedSupportingFile<T extends { id: number }>(
  list: T[] | undefined,
  selectedDocumentId: string | null
): T | undefined {
  if (!list || selectedDocumentId === null) return undefined;
  return list.find((f) => f.id.toString() === selectedDocumentId);
}

// ─── TIFF viewer helpers ───────────────────────────────────────────────────

export interface TiffDataDimensions {
  width: number;
  height: number;
  fitScale: number;
}

const ZOOM_PERCENT_MIN = 25;
const ZOOM_PERCENT_MAX = 500;

export function calculateDisplayDimensions(
  tiffData: TiffDataDimensions | null,
  zoom: number
): { width: number; height: number } {
  if (!tiffData) return { width: 0, height: 0 };
  const effectiveScale = tiffData.fitScale * zoom;
  return {
    width: Math.floor(tiffData.width * effectiveScale),
    height: Math.floor(tiffData.height * effectiveScale),
  };
}

export function getFitToWidthZoom(
  width: number,
  fitScale: number,
  containerWidth: number
): number {
  return containerWidth / (width * fitScale);
}

export function parseZoomFromInput(
  zoomInputValue: string,
  zoom: number,
  isEditingZoomInput: boolean
): number | null {
  const raw = isEditingZoomInput
    ? zoomInputValue
    : String(Math.round(zoom * 100));
  const parsed = parseInt(raw, 10);
  if (Number.isNaN(parsed)) return null;
  const percent = Math.max(
    ZOOM_PERCENT_MIN,
    Math.min(ZOOM_PERCENT_MAX, parsed)
  );
  return percent / 100;
}

export function printImageInNewWindow(
  fileName: string,
  imgDataUrl: string
): void {
  const printWindow = window.open('', '_blank');
  if (!printWindow) return;
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head><title>${fileName}</title></head>
      <body style="margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;">
        <img src="${imgDataUrl}" alt="${fileName}" style="max-width:100%;height:auto;" />
      </body>
    </html>
  `);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 250);
}

export function openDocumentInNewTab(url: string): void {
  window.open(url, '_blank');
}
